"use client";

import { ScrollTrigger } from "gsap/all";
import { useEffect, useMemo, useState } from "react";
import NavBar from "@/components/NavBar";
import HeroSection from "@/sections/HeroSection";
import Loader from "@/components/Loader";
import MessageSection from "@/sections/MessageSection";
import FlavorSection from "@/sections/FlavorSection";
import NutritionSection from "@/sections/NutritionSection";
import BenefitSection from "@/sections/BenefitSection";
import TestimonialSection from "@/sections/TestimonialSection";
import FooterSection from "@/sections/FooterSection";
import SmoothScrollLayout from "@/components/SmoothScrollLayout";
import { cards, flavorlists } from "@/constants";

const HERO_VIDEO_DESKTOP =
  "https://res.cloudinary.com/ddahd2kco/video/upload/v1770473006/bg-vid-kunafa_p37sqp.mp4";
const HERO_VIDEO_MOBILE =
  "https://res.cloudinary.com/ddahd2kco/video/upload/v1770557434/0fgcpsrynnrmr0cw7npsb8h54c_result__varibp.mp4";

const HomePageClient = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [assetsReady, setAssetsReady] = useState(false);
  const [loadProgress, setLoadProgress] = useState(0);
  const [showDeferredSections, setShowDeferredSections] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const criticalPreloadImages = useMemo(() => {
    return [
      "https://res.cloudinary.com/ddahd2kco/image/upload/v1770472227/logo_z0oeaw.jpg",
      "https://res.cloudinary.com/ddahd2kco/image/upload/v1770472251/slider-dip_naoon1.png",
    ];
  }, []);

  const deferredPreloadImages = useMemo(() => {
    const staticImages = [
      "https://res.cloudinary.com/ddahd2kco/image/upload/v1770472267/footer_jv22by.png",
    ];
    const flavorCardImages = flavorlists.flatMap((item) => [
      item.image,
      item.backImage,
      item.frontImage,
    ]);

    return Array.from(
      new Set([...staticImages, ...flavorCardImages, ...cards.map((item) => item.img)])
    );
  }, []);

  const criticalPreloadVideos = useMemo(() => {
    return Array.from(new Set([HERO_VIDEO_DESKTOP, HERO_VIDEO_MOBILE]));
  }, []);

  useEffect(() => {
    let cancelled = false;
    let settled = false;

    // Determine which video to preload based on screen width
    const isMobile = window.innerWidth < 768;
    const videoToPreload = isMobile ? HERO_VIDEO_MOBILE : HERO_VIDEO_DESKTOP;
    const videos = [videoToPreload];

    const totalAssets = criticalPreloadImages.length + videos.length;
    if (totalAssets === 0) {
      const raf = requestAnimationFrame(() => {
        setLoadProgress(100);
        setAssetsReady(true);
      });
      return () => cancelAnimationFrame(raf);
    }

    let loadedCount = 0;

    const markLoaded = () => {
      loadedCount += 1;
      const nextProgress = Math.min(100, Math.round((loadedCount / totalAssets) * 100));
      if (!cancelled) {
        setLoadProgress(nextProgress);
      }
      if (loadedCount >= totalAssets && !settled) {
        settled = true;
        if (!cancelled) {
          setAssetsReady(true);
        }
      }
    };

    const loadImage = (src: string) =>
      new Promise<void>((resolve) => {
        const img = new Image();
        const done = () => resolve();
        img.onload = done;
        img.onerror = done;
        img.src = src;
      }).finally(markLoaded);

    const loadVideo = (src: string) =>
      new Promise<void>((resolve) => {
        const video = document.createElement("video");
        let finished = false;
        const done = () => {
          if (finished) return;
          finished = true;
          video.removeEventListener("loadedmetadata", done);
          video.removeEventListener("error", done);
          resolve();
        };
        video.preload = "metadata";
        video.muted = true;
        video.playsInline = true;
        video.addEventListener("loadedmetadata", done, { once: true });
        video.addEventListener("error", done, { once: true });
        video.src = src;
        video.load();

        window.setTimeout(done, 10000);
      }).finally(markLoaded);

    const timeoutId = window.setTimeout(() => {
      if (!settled && !cancelled) {
        settled = true;
        setAssetsReady(true);
      }
    }, 22000);

    criticalPreloadImages.forEach((src) => loadImage(src));
    // Only load the relevant video
    videos.forEach((src) => loadVideo(src));

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [criticalPreloadImages, criticalPreloadVideos]);

  useEffect(() => {
    if (isLoading) return;

    const warmImage = (src: string) => {
      const img = new Image();
      img.decoding = "async";
      img.src = src;
    };

    const timer = window.setTimeout(() => {
      deferredPreloadImages.forEach(warmImage);
    }, 1200);

    return () => {
      window.clearTimeout(timer);
    };
  }, [isLoading, deferredPreloadImages]);

  useEffect(() => {
    if (isLoading) return;

    const timer = window.setTimeout(() => {
      setShowDeferredSections(true);
    }, 650);

    return () => {
      window.clearTimeout(timer);
    };
  }, [isLoading]);

  useEffect(() => {
    if (!isLoading) {
      window.scrollTo(0, 0);
      const timer = window.setTimeout(() => {
        ScrollTrigger.clearScrollMemory();
        ScrollTrigger.sort();
        ScrollTrigger.refresh(true);
      }, 500);
      return () => window.clearTimeout(timer);
    }
  }, [isLoading]);

  useEffect(() => {
    if (isLoading || !showDeferredSections) return;

    let raf1 = 0;
    let raf2 = 0;
    let fontsCancelled = false;

    const refreshScrollState = () => {
      ScrollTrigger.clearScrollMemory();
      ScrollTrigger.sort();
      ScrollTrigger.refresh(true);
    };

    // Wait for deferred sections to paint before recalculating pin starts/ends.
    raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => {
        refreshScrollState();
      });
    });

    // Re-run once after layout settles to keep long pinned timelines accurate.
    const settleTimer = window.setTimeout(refreshScrollState, 450);

    // Large heading font swaps can shift trigger offsets on first load.
    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        if (!fontsCancelled) {
          refreshScrollState();
        }
      });
    }

    return () => {
      fontsCancelled = true;
      window.cancelAnimationFrame(raf1);
      window.cancelAnimationFrame(raf2);
      window.clearTimeout(settleTimer);
    };
  }, [isLoading, showDeferredSections]);

  const handleLoadingComplete = () => {
    setIsLoading(false);
  };

  return (
    <main>
      {!isLoading && <NavBar />}
      {isLoading && (
        <Loader
          progress={loadProgress}
          isReady={assetsReady}
          onLoadingComplete={handleLoadingComplete}
        />
      )}
      {!isLoading && (
        <SmoothScrollLayout contentClassName="bg-[#FEF3F0]">
          <HeroSection isLoading={isLoading} />
          <MessageSection />
          {showDeferredSections && (
            <>
              <FlavorSection />
              <NutritionSection />
              <div>
                <BenefitSection />
                {/* <TestimonialSection /> */}
              </div>
              <FooterSection dipImg={true} />
            </>
          )}
        </SmoothScrollLayout>
      )}
    </main>
  );
};

export default HomePageClient;
